import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, redirectToLogin } from '../lib/auth';
import { api } from '../lib/api';
import { GedeeldAccountVraag, moetPersoonVragen } from './GedeeldAccountVraag';
import { startFullTour, resumeIfActive, getTourLang, setTourLang, type TourLang } from '../lib/tour';

// Lijn-iconen: één getekende set (24-grid, 1.75 lijn, ronde uiteinden) in plaats van unicode-tekens,
// zodat elk icoon even zwaar oogt. Ook gebruikt door pagina's (download, mail, waarschuwing, …).
const ICON_PATHS = {
  dashboard: (
    <>
      <path d="M3.5 10.5 12 3.75l8.5 6.75" />
      <path d="M5.75 9v10.25h12.5V9" />
      <path d="M10 19.25v-5.5h4v5.5" />
    </>
  ),
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  tedoen: (
    <>
      <path d="M3.75 13.25h4.5l1.5 2.5h4.5l1.5-2.5h4.5" />
      <path d="M6.25 4.75h11.5l2.5 8.5v5.5a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-5.5z" />
    </>
  ),
  maand: (
    <>
      <rect x="3.75" y="5" width="16.5" height="15.25" rx="2" />
      <path d="M3.75 10h16.5M8.25 3v4M15.75 3v4" />
      <path d="M8 14h2M14 14h2M8 17h2" />
    </>
  ),
  extra: (
    <>
      <rect x="3.75" y="3.75" width="16.5" height="16.5" rx="2.5" />
      <path d="M12 7.25v5M9.5 9.75h5M9.5 16.25h5" />
    </>
  ),
  prijslijst: (
    <>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" strokeWidth={2.5} />
    </>
  ),
  geleerd: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 12h.01" strokeWidth={2.5} />
    </>
  ),
  onderaannemer: (
    <>
      <path d="M10 10.5V5.25a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5.25" />
      <path d="M4.75 15v-2.25A6.5 6.5 0 0 1 10 6.4" />
      <path d="M14 6.4a6.5 6.5 0 0 1 5.25 6.35V15" />
      <rect x="2.75" y="15" width="18.5" height="3.75" rx="1.25" />
    </>
  ),
  gebruikers: (
    <>
      <circle cx="9.25" cy="8.5" r="3.25" />
      <path d="M3.5 19.25a5.75 5.75 0 0 1 11.5 0" />
      <path d="M15.5 5.5a3.25 3.25 0 0 1 0 6.1" />
      <path d="M17.25 13.75a5.75 5.75 0 0 1 3.25 5.5" />
    </>
  ),
  tags: (
    <>
      <path d="M3.75 12.6V5a1.25 1.25 0 0 1 1.25-1.25h7.6a1.25 1.25 0 0 1 .88.37l7.4 7.4a1.25 1.25 0 0 1 0 1.77l-7.6 7.6a1.25 1.25 0 0 1-1.77 0l-7.4-7.4a1.25 1.25 0 0 1-.36-.89z" />
      <circle cx="8.5" cy="8.5" r="1.5" />
    </>
  ),
  activiteit: (
    <>
      <path d="M3.75 12a8.25 8.25 0 1 0 2.4-5.8" />
      <path d="M3.75 3.75v3.5h3.5" />
      <path d="M12 7.75V12l2.75 1.75" />
    </>
  ),
  account: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <circle cx="12" cy="10" r="2.75" />
      <path d="M6.9 18.1a5.75 5.75 0 0 1 10.2 0" />
    </>
  ),
  rondleiding: (
    <>
      <path d="M9 4.75 3.75 6.9v12.35L9 17.1l6 2.15 5.25-2.15V4.75L15 6.9z" />
      <path d="M9 4.75V17.1M15 6.9v12.35" />
    </>
  ),
  chevron: <path d="m7 10 5 5 5-5" />,
  check: <path d="m5.5 12.5 4 4 9-9" />,
  uitloggen: (
    <>
      <path d="M10 4.75H6.75a2 2 0 0 0-2 2v10.5a2 2 0 0 0 2 2H10" />
      <path d="m15 16.25 4.25-4.25L15 7.75" />
      <path d="M19.25 12h-9.5" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  sluiten: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  download: (
    <>
      <path d="M12 4.25v10.5" />
      <path d="m7.75 10.5 4.25 4.25 4.25-4.25" />
      <path d="M4.75 19.25h14.5" />
    </>
  ),
  mail: (
    <>
      <rect x="3.75" y="5.75" width="16.5" height="12.5" rx="2" />
      <path d="m4.5 7 7.5 5.75L19.5 7" />
    </>
  ),
  chat: <path d="M5.25 18.75 6.4 15.3A7.25 7.25 0 1 1 9 17.85z" />,
  waarschuwing: (
    <>
      <path d="M10.3 4.6 3.4 16.75a1.95 1.95 0 0 0 1.7 2.9h13.8a1.95 1.95 0 0 0 1.7-2.9L13.7 4.6a1.95 1.95 0 0 0-3.4 0z" />
      <path d="M12 9.5v3.75M12 16.25h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  pijlRechts: <path d="m10 7 5 5-5 5" />,
  zoek: (
    <>
      <circle cx="11" cy="11" r="6.25" />
      <path d="m15.75 15.75 3.5 3.5" />
    </>
  ),
  koppelen: (
    <>
      <path d="M4.75 8.75h13l-3.5-3.5" />
      <path d="M19.25 15.25h-13l3.5 3.5" />
    </>
  ),
} as const;

export type IconName = keyof typeof ICON_PATHS;

/** Eén lijn-iconenset voor de hele app (24-grid, 1.75 lijn). Kleur volgt `currentColor`. */
export function Icon({ name, size = 20, strokeWidth = 1.75, className = '' }: { name: IconName; size?: number; strokeWidth?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className}`}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  adminOnly?: boolean;
  /** Enkel voor rollen met beheer (labels, standaardprijzen) — SPEC 27okt 7. */
  beheerOnly?: boolean;
  /** Admins en rollen met beheer (prijslijsten, geleerde matches). */
  adminOfBeheer?: boolean;
  /** De enige actie in de navigatie: krijgt een gevuld oranje blokje i.p.v. een lijn-icoon. */
  accent?: boolean;
  /** Toont een aantal naast het label (open meldingen in "Te doen"). */
  teller?: 'workflow';
}

// Dagelijks werk bovenaan; beheerpagina's in een eigen groep, zodat het menu in één oogopslag leest.
const NAV_WERK: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard' },
  { to: '/project/new', label: 'Nieuw project', icon: 'plus', accent: true },
  { to: '/te-doen', label: 'Te doen', icon: 'tedoen', teller: 'workflow' },
  { to: '/month', label: 'Maand-overzicht', icon: 'maand' },
  { to: '/extra-regels', label: 'Extra werken & kosten', icon: 'extra' },
  { to: '/activity', label: 'Activiteit', icon: 'activiteit' },
];
const NAV_BEHEER: NavItem[] = [
  { to: '/pricelists', label: 'Prijslijsten', icon: 'prijslijst', adminOfBeheer: true },
  { to: '/learned', label: 'Geleerde matches', icon: 'geleerd', adminOfBeheer: true },
  { to: '/tags', label: 'Labels', icon: 'tags', beheerOnly: true },
  { to: '/ploegen', label: 'Onderaannemers', icon: 'onderaannemer', adminOnly: true },
  { to: '/users', label: 'Gebruikers', icon: 'gebruikers', adminOnly: true },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const [tourMenuOpen, setTourMenuOpen] = useState(false);
  const [tourLang, setTourLangState] = useState<TourLang>(getTourLang());
  // Mobiel: de zijbalk schuift in als lade.
  const [menuOpen, setMenuOpen] = useState(false);
  // Gedeeld account (27okt 1): vraag wie er werkt, tenzij al gekend of overgeslagen in deze sessie.
  const [persoonVraagOpen, setPersoonVraagOpen] = useState(() => moetPersoonVragen(user));
  useEffect(() => {
    if (moetPersoonVragen(user)) setPersoonVraagOpen(true);
  }, [user]);

  const handleLogout = async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    signOut();
    redirectToLogin();
  };

  const launchTour = (lang: TourLang) => {
    setTourLang(lang);
    setTourLangState(lang);
    setTourMenuOpen(false);
    setMenuOpen(false);
    startFullTour(lang);
  };

  // Resume de tour als die actief is voor deze pagina (na navigation tussen
  // segments). 250ms vertraging in resumeIfActive geeft DOM tijd om te
  // renderen voordat driver.js selectors gaat zoeken.
  useEffect(() => {
    resumeIfActive();
    setMenuOpen(false);
  }, [location.pathname]);

  // Esc sluit de mobiele lade en het rondleidingsmenu.
  useEffect(() => {
    if (!menuOpen && !tourMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setTourMenuOpen(false);
      setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, tourMenuOpen]);

  // Open meldingen in "Te doen" (blok C): elke 5 minuten verversen, de polling zelf draait elk uur.
  const workflowQ = useQuery({ queryKey: ['workflow', 'status'], queryFn: () => api.workflow.status(), refetchInterval: 5 * 60 * 1000, staleTime: 60 * 1000 });
  const openMeldingen = workflowQ.data?.open ?? 0;

  const zichtbaar = (n: NavItem) =>
    (!n.adminOnly || isAdmin) && (!n.beheerOnly || !!user?.magBeheren) && (!n.adminOfBeheer || isAdmin || !!user?.magBeheren);
  const beheer = NAV_BEHEER.filter(zichtbaar);
  const naam = user?.gedeeldAccount ? accountNaam(user.displayName) : user?.displayName ?? user?.email;
  const rolTekst = [user?.rolNaam, isAdmin ? 'Admin' : null].filter(Boolean).join(' · ') || user?.email;

  return (
    <div className="min-h-screen bg-canvas md:flex">
      {/* Mobiel: compacte balk met merk en menuknop. Vanaf md staat de zijbalk altijd open. */}
      <header className="md:hidden sticky top-0 z-40 flex h-14 items-center justify-between gap-3 bg-primary px-4 text-white">
        <Merk />
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="-mr-2 rounded-md p-2 text-white/80 hover:bg-white/10 hover:text-white"
          aria-label="Menu openen"
          aria-expanded={menuOpen}
          aria-controls="app-zijbalk"
        >
          <Icon name="menu" size={22} />
        </button>
      </header>
      {menuOpen && <div className="fixed inset-0 z-40 bg-primary-800/40 md:hidden" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

      <aside
        id="app-zijbalk"
        className={`fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col bg-primary text-white transition-[transform,visibility] duration-200 ease-out md:visible md:sticky md:top-0 md:z-auto md:h-screen md:translate-x-0 ${
          menuOpen ? 'translate-x-0' : 'max-md:invisible -translate-x-full'
        }`}
      >
        <div className="flex h-[72px] shrink-0 items-center justify-between gap-2 border-b border-white/10 px-5">
          <Merk />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="-mr-2 rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white md:hidden"
            aria-label="Menu sluiten"
          >
            <Icon name="sluiten" size={20} />
          </button>
        </div>

        <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4" aria-label="Hoofdmenu">
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {NAV_WERK.filter(zichtbaar).map((n) => <NavRegel key={n.to} item={n} aantal={n.teller === 'workflow' ? openMeldingen : undefined} />)}
            {beheer.length > 0 && (
              <>
                <li className="px-3 pb-1.5 pt-5 text-xs font-semibold text-white/55" aria-hidden="true">Beheer</li>
                {beheer.map((n) => <NavRegel key={n.to} item={n} />)}
              </>
            )}
          </ul>
        </nav>

        <div className="shrink-0 space-y-1 border-t border-white/10 p-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setTourMenuOpen((v) => !v)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-white/75 transition-colors hover:bg-white/5 hover:text-white"
              title={tourLang === 'nl' ? 'Start een rondleiding van deze pagina' : 'Start a guided tour of this page'}
              aria-haspopup="menu"
              aria-expanded={tourMenuOpen}
            >
              <Icon name="rondleiding" className="text-white/60" />
              <span className="flex-1 text-left">{tourLang === 'nl' ? 'Rondleiding' : 'Tour'}</span>
              <Icon name="chevron" size={16} strokeWidth={2} className={`text-white/50 transition-transform ${tourMenuOpen ? '' : 'rotate-180'}`} />
            </button>
            {tourMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setTourMenuOpen(false)} />
                <div role="menu" className="absolute inset-x-0 bottom-full z-50 mb-1 overflow-hidden rounded-md border border-border bg-white py-1 text-primary shadow-lg">
                  <TaalKeuze label="Nederlands" actief={tourLang === 'nl'} onClick={() => launchTour('nl')} />
                  <TaalKeuze label="English" actief={tourLang === 'en'} onClick={() => launchTour('en')} />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            <NavLink
              to="/account"
              title="Mijn account"
              className={({ isActive }) =>
                `min-w-0 flex-1 rounded-md px-3 py-2 transition-colors focus-visible:shadow-[inset_0_0_0_2px_rgba(255,255,255,0.7)] ${isActive ? 'bg-white/10' : 'hover:bg-white/5'}`
              }
            >
              <span className="sr-only">Account: </span>
              <span className="block truncate text-sm font-semibold text-white">{naam}</span>
              <span className="block truncate text-xs text-white/60">{rolTekst}</span>
            </NavLink>
            <button
              type="button"
              onClick={handleLogout}
              className="shrink-0 rounded-md p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label={tourLang === 'nl' ? 'Uitloggen' : 'Logout'}
              title={tourLang === 'nl' ? 'Uitloggen' : 'Logout'}
            >
              <Icon name="uitloggen" size={18} />
            </button>
          </div>
          {user?.gedeeldAccount && (
            <button
              type="button"
              onClick={() => setPersoonVraagOpen(true)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs text-white/70 transition-colors hover:bg-white/5 hover:text-white"
              title={`Gedeeld account ${user.email}: ${user.persoon ? `${user.persoon} werkt. Klik om te wijzigen.` : 'nog niet gezegd wie er werkt. Klik om je naam te kiezen.'}`}
            >
              <Icon name="gebruikers" size={16} className="text-white/50" />
              {user.persoon ? <span className="truncate font-semibold text-white">{user.persoon}</span> : <span className="italic">wie werkt er?</span>}
            </button>
          )}
        </div>
      </aside>

      <main className="mx-auto w-full min-w-0 max-w-[1440px] flex-1 px-6 py-6 lg:px-7 lg:py-7">{children}</main>
      {persoonVraagOpen && user?.gedeeldAccount && <GedeeldAccountVraag onSluit={() => setPersoonVraagOpen(false)} />}
    </div>
  );
}

/** Merkblok: oranje beeldmerk met de app-naam en "Recotex" eronder. */
function Merk() {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex shrink-0 items-center justify-center rounded-md bg-accent" style={{ width: 34, height: 34 }}>
        <img src="/r-kapvorm-white.png" alt="" style={{ height: 20, width: 20 }} className="object-contain" />
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block truncate text-[15px] font-bold text-white">Calculaties</span>
        <span className="block truncate text-xs text-white/60">Recotex</span>
      </span>
    </div>
  );
}

function NavRegel({ item: n, aantal }: { item: NavItem; aantal?: number }) {
  return (
    <li>
      <NavLink
        to={n.to}
        end={n.to === '/'}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:shadow-[inset_0_0_0_2px_rgba(255,255,255,0.7)] ${
            isActive ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/5 hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {/* Actieve plaats: een kort oranje streepje tegen de linkerrand van de pil. */}
            {isActive && <span className="absolute left-0 top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-r-full bg-accent" aria-hidden="true" />}
            {n.accent ? (
              <span
                className="flex items-center justify-center rounded-xs bg-accent text-white transition-colors group-hover:bg-accent-600"
                style={{ width: 20, height: 20 }}
              >
                <Icon name={n.icon} size={14} strokeWidth={2.5} />
              </span>
            ) : (
              <Icon name={n.icon} className={isActive ? 'text-white' : 'text-white/60 transition-colors group-hover:text-white/90'} />
            )}
            <span className="truncate">{n.label}</span>
            {!!aantal && (
              <span className="ml-auto rounded-pill bg-accent-600 px-1.5 text-xs font-bold leading-5 text-white tabular-nums" aria-label={`${aantal} open`}>
                {aantal}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

function TaalKeuze({ label, actief, onClick }: { label: string; actief: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-primary-50"
    >
      <span>{label}</span>
      {actief && <Icon name="check" size={16} strokeWidth={2.25} className="text-accent-600" />}
    </button>
  );
}

/** "Calculaties (gedeeld account)" → "Calculaties": de persoon staat er los naast. */
function accountNaam(naam: string): string {
  return naam.replace(/\s*\([^)]*\)\s*$/, '').trim() || naam;
}
